import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable } from "rxjs";
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  constructor(private http: HttpClient, private authService: AuthService) { }

  /**
   * Returns HttpHeaders with Authorization if token exists. Handles async token retrieval.
   */
  private async getAuthHeaders(customHeaders: any = {}): Promise<HttpHeaders> {
    const token = await this.authService.getAccessToken();
    let headers = new HttpHeaders(customHeaders);
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  /**
   * Generic method to make HTTP requests with authentication.
   */
  private requestWithAuth<T>(
    method: 'get' | 'post' | 'put' | 'patch' | 'delete',
    endpoint: string,
    paramsOrBody: any = {},
    customHeaders: any = {}
  ): Observable<T> {
    return new Observable<T>((subscriber) => {
      let isUnsubscribed = false;
      this.getAuthHeaders(customHeaders)
        .then((headers) => {
          if (isUnsubscribed) return;
          let req$;
          switch (method) {
            case 'get':
              req$ = this.http.get<T>(endpoint, {
                params: paramsOrBody,
                headers,
              });
              break;
            case 'post':
              req$ = this.http.post<T>(endpoint, paramsOrBody, { headers });
              break;
            case 'put':
              req$ = this.http.put<T>(endpoint, paramsOrBody, { headers });
              break;
            case 'patch':
              req$ = this.http.patch<T>(endpoint, paramsOrBody, { headers });
              break;
            case 'delete':
              req$ = this.http.delete<T>(endpoint, {
                params: paramsOrBody,
                headers,
              });
              break;
            default:
              subscriber.error(new Error('Invalid HTTP method'));
              return;
          }
          const sub = req$.subscribe({
            next: (value) => subscriber.next(value),
            error: (err) => subscriber.error(err),
            complete: () => subscriber.complete(),
          });
          subscriber.add(() => sub.unsubscribe());
        })
        .catch((err) => {
          subscriber.error(err);
        });
      return () => {
        isUnsubscribed = true;
      };
    });
  }

  get<T>(endpoint: string, params = {}, customHeaders = {}): Observable<T> {
    return this.requestWithAuth('get', endpoint, params, customHeaders);
  }

  post<T>(endpoint: string, params = {}, customHeaders = {}): Observable<T> {
    return this.requestWithAuth('post', endpoint, params, customHeaders);
  }

  put<T>(endpoint: string, params = {}, customHeaders = {}): Observable<T> {
    return this.requestWithAuth('put', endpoint, params, customHeaders);
  }

  patch<T>(endpoint: string, params = {}, customHeaders = {}): Observable<T> {
    return this.requestWithAuth('patch', endpoint, params, customHeaders);
  }

  delete<T>(endpoint: string, params = {}, customHeaders = {}): Observable<T> {
    return this.requestWithAuth('delete', endpoint, params, customHeaders);
  }
}
